import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import gql from 'graphql-tag';
import _ from 'lodash';
import { getBongData } from './__queries__';
import BongGroup, { BongGroupOrderInfo } from './BongGroup';
import { LocalItem, TicketMessage } from '~Types';
import { useStorage } from '~Components/Storage';
import { ORDER_ORIGIN } from '@baemingo/order-status-events/dist/types';
import { SERVICE_TYPE } from '~../__queries__';
import ControlledFlow from './ControlledFlow';

const GET_STATIONS_AND_CATEGORIES = gql`
  query getBongData($companyId: ID!, $venueId: String!) {
    stations: allStations(filter: { company: { id: $companyId } }) {
      id
      name
      showAllItems
      categories {
        id
        name
      }
      items {
        id
        title
      }
      origin
      serviceType
      showAllItems
    }
    categories: allCategories(filter: { company: { id: $companyId } }) {
      id
      name
      nameLang {
        id
        en
        se
        es
        no
      }
    }
    floorplans: allFloorPlans(filter: { company: { id: $companyId } }) {
      id
      name
      tables {
        id
      }
    }
    courseTypes: allCourseTypes(filter: { company: { id: $companyId }, deleted: false }) {
      id
      name
      label
      deleted
      order
    }
    operativeViewer {
      id
      ticketTemplates(venueId: $venueId) {
        id
        companyId
        name
        venueIds
        stationIds
        layout {
          id
          type
          height
        }
        styling
        createdAt
        updatedAt
        updatedBy
      }
    }
  }
`;

interface OrderInfo {
  title: string;
  date: string;
  serviceType: string;
  estimatedTime: string | undefined;
  desiredTime: string | undefined;
  deliveryLocation: string | undefined;
  items: LocalItem[];
  floorplan?: string;
  tableName?: string;
  tableId?: string | null;
  orderNumber?: string;
  messages?: LocalItem[];
  staffId?: string;
  coverage?: number;
  comment?: string;
  origin?: ORDER_ORIGIN | null;
  ticketMessages?: TicketMessage[];
}

interface Props {
  onlyDigitalBongs?: boolean; //TODO: don't think this is needed anymore
}

const BongMaster: React.FC<Props> = ({ onlyDigitalBongs }) => {
  const [storage] = useStorage();
  const companyId = useSelector((state) => state.company);
  const dispatch = useDispatch();
  const bongPrintingHalted = useSelector((state) => state.BongPrintingHalted);
  const bongGroups = useSelector((state) => state.POSBongGroups);
  const { i18n } = useI18n();
  const logger = useLogger('bongmaster');
  const venueId = useSelector((state) => state.venueId);

  const companyDataQuery = useQuery<getBongData>(GET_STATIONS_AND_CATEGORIES, {
    skip: !companyId || !venueId,
    variables: { companyId, venueId },
  });

  useEffect(() => {
    if (!companyDataQuery.loading && !companyDataQuery.data && venueId && companyId) {
      logger.error(
        'Bongmaster - Company data query data undefined - refetching. ' +
          (companyDataQuery.error?.message || 'Unknown error'),
      );
      setTimeout(() => companyDataQuery.refetch(), 1500);
    }
  }, [companyDataQuery]);

  // These two states handle "collect" / "samlingsbong"
  const [mergedGroups, setMergedGroups] = useState<typeof bongGroups>([]);
  const [mergedGroupsForPrint, setMergedGroupsForPrint] = useState<OrderInfo | null>(null);

  // If bong printing is halted, merge all bong groups from POS into one
  useEffect(() => {
    if (!bongPrintingHalted) {
      return;
    }

    if (bongGroups.length === 0) {
      return;
    }

    const lastGroup = bongGroups[bongGroups.length - 1];
    if (lastGroup.origin !== ORDER_ORIGIN.POS) {
      return;
    }

    // Check if the group is already merged
    const mergedGroup = mergedGroups.find((g) => g.id === lastGroup.id);
    if (mergedGroup) {
      return;
    }

    // Merge the group and set it as bonged
    setMergedGroups([...mergedGroups, lastGroup]);
  }, [bongGroups]);

  /**
   * Trigger a print of all merged bong groups when printing is not halted anymore
   */
  useEffect(() => {
    if (bongPrintingHalted) {
      return;
    }

    const forPrint = mergedGroups.reduce<OrderInfo | null>((obj, group) => {
      if (!group) {
        return obj;
      }

      let returnValue = obj;

      const orderNumberTitle = group.order?.orderNumber?.includes('•')
        ? group.order?.orderNumber
        : '#' + group.order?.orderNumber;

      const floorplan = group.order?.tableId
        ? companyDataQuery?.data?.floorplans.find((f) =>
            f.tables.some((t) => t.id === group.order?.tableId),
          )
        : undefined;

      if (!returnValue) {
        returnValue = {
          title: group.order?.chequeName
            ? i18n('TSR.Table') + ' ' + group.order?.chequeName
            : orderNumberTitle,
          date: new Date().toISOString(),
          serviceType: storage.enableServiceTypeDelivery
            ? SERVICE_TYPE.DELIVERY
            : group.serviceType ?? '',
          desiredTime: group.order?.desiredTime,
          estimatedTime: undefined,
          deliveryLocation: undefined,
          items: [],
          messages: [],
          coverage: group.order?.coverage,
          staffId: group.order?.staffId,
          tableName: group.order?.chequeName,
          tableId: group.tableId,
          floorplan: floorplan?.name,
          orderNumber: group.order?.orderNumber,
          comment: group.order?.comment,
          origin: group.origin || ORDER_ORIGIN.POS, // All Express- & App orders sets origin. So we can assume that if origin is not set, it's a POS order.
          ticketMessages: group.ticketMessages,
        };
      }

      returnValue.items = [...group.items, ...returnValue.items];
      returnValue.messages = _.unionBy(group.messages, returnValue.messages, 'courseType');

      return returnValue;
    }, null);

    mergedGroups.forEach((g) => {
      dispatch({
        type: 'BONG_GROUP_BONGED',
        id: g.id,
      });
    });

    setMergedGroups([]);
    setMergedGroupsForPrint(forPrint);
  }, [bongPrintingHalted]);

  if (!companyDataQuery?.data?.stations) {
    logger.info('[DEBUG MODE] No station found, skipping bong printing');
    return null;
  }

  const shouldUseNewPrinterFlow =
    storage.useNewPrinterFlow || companyId === 'cld8h9zcglx3d0b872fdyr6c4'; // Andys burgers

  return (
    <>
      {bongGroups.map((group) => {
        /*
         *  If we've collected bongs, dont print here - they will be printed
         *  via "mergedGroupsForPrint" below
         */
        const isMergedGroup = mergedGroups.find((g) => g.id === group.id);
        if (isMergedGroup || (bongPrintingHalted && group.origin === ORDER_ORIGIN.POS)) {
          logger.info('[DEBUG MODE] Skipping bong printing for group');
          return;
        }

        const floorplan = group.order?.tableId
          ? companyDataQuery?.data?.floorplans.find((f) =>
              f.tables.some((t) => t.id === group.order?.tableId),
            )
          : undefined;

        const orderNumberTitle = group.order?.orderNumber?.includes('•')
          ? group.order?.orderNumber
          : '#' + group.order?.orderNumber;

        const title = group.order?.chequeName
          ? i18n('TSR.Table') + ' ' + group.order?.chequeName
          : orderNumberTitle;

        const orderInfo: BongGroupOrderInfo = {
          title,
          date: group.order?.estimatedFinishTime ?? new Date().toISOString(),
          serviceType: storage.enableServiceTypeDelivery
            ? SERVICE_TYPE.DELIVERY
            : group.serviceType ?? SERVICE_TYPE.EAT_HERE,
          estimatedTime: group.order?.estimatedFinishTime,
          desiredTime: group.order?.desiredTime,
          deliveryLocation: group.order?.deliveryLocation,
          coverage: group.order?.coverage,
          staffId: group.order?.staffId,
          tableName: group.order?.chequeName,
          tableId: group.tableId,
          floorplan: floorplan?.name,
          orderNumber: group.order?.orderNumber,
          realId: group.order?.realId,
          comment: group.order?.comment,
          origin: group.origin || ORDER_ORIGIN.POS, // All Express- & App orders sets origin. So we can assume that if origin is not set, it's a POS order.
        };

        if (shouldUseNewPrinterFlow) {
          return (
            <ControlledFlow
              key={group.id}
              groupId={group.id}
              items={group.items}
              messages={group.messages}
              ticketMessages={group.ticketMessages}
              stations={companyDataQuery?.data?.stations || []}
              categories={companyDataQuery?.data?.categories || []}
              allCourseTypes={companyDataQuery?.data?.courseTypes || []}
              ticketTemplates={companyDataQuery?.data?.operativeViewer?.ticketTemplates || []}
              orderInfo={orderInfo}
              onlyPhysicalBongs={group.onlyPhysical}
              skipConfirmation={group.skipConfirmation}
              onCompleted={() => {
                dispatch({
                  type: 'BONG_GROUP_BONGED',
                  id: group.id,
                });
              }}
            />
          );
        } else {
          return (
            <BongGroup
              key={group.id}
              groupId={group.id}
              items={group.items}
              messages={group.messages}
              ticketMessages={group.ticketMessages}
              stations={companyDataQuery?.data?.stations || []}
              categories={companyDataQuery?.data?.categories || []}
              allCourseTypes={companyDataQuery?.data?.courseTypes || []}
              orderInfo={orderInfo}
              onlyPhysicalBongs={group.onlyPhysical}
              onlyDigitalBongs={onlyDigitalBongs}
              skipConfirmation={group.skipConfirmation}
              onCompleted={() => {
                dispatch({
                  type: 'BONG_GROUP_BONGED',
                  id: group.id,
                });
              }}
            />
          );
        }
      })}

      {mergedGroupsForPrint &&
        (shouldUseNewPrinterFlow ? (
          <ControlledFlow
            key="merged"
            groupId="merged"
            items={mergedGroupsForPrint.items}
            messages={mergedGroupsForPrint.messages}
            allCourseTypes={companyDataQuery?.data?.courseTypes || []}
            ticketMessages={mergedGroupsForPrint.ticketMessages}
            stations={companyDataQuery?.data?.stations || []}
            categories={companyDataQuery?.data?.categories || []}
            ticketTemplates={companyDataQuery?.data?.operativeViewer?.ticketTemplates || []}
            onlyPhysicalBongs={false}
            orderInfo={mergedGroupsForPrint}
            onCompleted={() => {
              setMergedGroupsForPrint(null);
            }}
          />
        ) : (
          <BongGroup
            key="merged"
            groupId="merged"
            items={mergedGroupsForPrint.items}
            messages={mergedGroupsForPrint.messages}
            ticketMessages={mergedGroupsForPrint.ticketMessages}
            stations={companyDataQuery?.data.stations}
            categories={companyDataQuery?.data.categories}
            allCourseTypes={companyDataQuery?.data.courseTypes}
            onlyPhysicalBongs={false}
            onlyDigitalBongs={onlyDigitalBongs}
            orderInfo={mergedGroupsForPrint}
            onCompleted={() => {
              setMergedGroupsForPrint(null);
            }}
          />
        ))}
    </>
  );
};

export default BongMaster;
